import { Box, Button } from "@mui/material";
import { DateRangeCalendar } from "@mui/x-date-pickers-pro/DateRangeCalendar";
// eslint-disable-next-line import/order
import { TimeField } from "@mui/x-date-pickers/TimeField";
import dayjs, { Dayjs } from "dayjs";
import React, { useMemo, useState } from "react";

import { dateValues } from "components/dataGridFilter/FiltersData";
import { Typography } from "new-components/typography/Typography";

import { DateTab } from "./DateTab";
import { StartEndDateTimeProps } from "./types";
import { formatDateTime, getDateTime } from "./utils";

interface PickerProps {
  handleChangeDateField: (newValue: string) => void;
  handleClose: () => void;
  value: string;
}

export function Picker(props: PickerProps) {
  const { handleClose, handleChangeDateField, value } = props;

  const [error, setError] = useState<string>("");
  const [valueTab, setValueTab] = useState<string>("start");

  const isEditingCustom = useMemo(() => !dateValues.includes(value), [value]);

  const separateDate = (value: string) => value.split("_");

  const [startDateTime, setStartDateTime] = useState<StartEndDateTimeProps>({
    date: isEditingCustom ? dayjs(separateDate(value)[0]) : null,
    time: isEditingCustom ? dayjs(separateDate(value)[0]) : null,
  });
  const [endDateTime, setEndDateTime] = useState<StartEndDateTimeProps>({
    date: isEditingCustom ? dayjs(separateDate(value)[1]) : null,
    time: isEditingCustom ? dayjs(separateDate(value)[1]) : null,
  });

  const handleChangeDate = (newValue: any, selectionState: any) => {
    if (valueTab === "start") {
      setStartDateTime({
        date: selectionState === "partial" ? newValue[0] : newValue[1],
        time: startDateTime.time === null ? dayjs("2022-04-17T00:00") : startDateTime.time,
      });
      setValueTab("end");
      return;
    }
    setEndDateTime({
      date: selectionState === "partial" ? newValue[0] : newValue[1],
      time: endDateTime.time === null ? dayjs("2022-04-17T23:59:59") : endDateTime.time,
    });
  };

  const handleChangeTime = (newValue: any) => {
    if (valueTab === "start") {
      setStartDateTime({
        ...startDateTime,
        time: newValue,
      });
      return;
    }
    setEndDateTime({
      ...endDateTime,
      time: newValue,
    });
  };

  const validateRangeDate = (startDate: Dayjs, endDate: Dayjs) => startDate.isSameOrBefore(endDate);

  const handleSave = () => {
    const startDate = getDateTime(dayjs(startDateTime.date), dayjs(startDateTime.time));
    const endDate = getDateTime(dayjs(endDateTime.date), dayjs(endDateTime.time));

    if (!validateRangeDate(startDate, endDate)) {
      setError("End date cannot be before the start date.");
      return;
    }

    setError("");
    const startDateTimeFormatted = formatDateTime(startDate);
    const endDateTimeFormatted = formatDateTime(endDate);
    const formatToString = `${startDateTimeFormatted}_${endDateTimeFormatted}`;
    handleChangeDateField(formatToString);
    handleClose();
  };

  return (
    <Box sx={{ width: "auto", padding: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "start" }}>
        <DateTab
          label="start"
          valueTab={valueTab}
          handleChange={() => setValueTab("start")}
          dateValues={startDateTime}
        />
        <DateTab label="end" valueTab={valueTab} handleChange={() => setValueTab("end")} dateValues={endDateTime} />
      </Box>

      {valueTab === "start" ? (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box sx={{ width: "315px", height: "300px" }}>
            <DateRangeCalendar
              calendars={1}
              value={[startDateTime.date, endDateTime.date]}
              onChange={handleChangeDate}
              rangePosition="start"
            />
          </Box>
          <Box sx={{ width: "225px", mt: "48px" }}>
            <TimeField label="Time" value={startDateTime.time} onChange={handleChangeTime} format="HH:mm:ss" />
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box sx={{ width: "315px", height: "300px" }}>
            <DateRangeCalendar
              calendars={1}
              value={[startDateTime.date, endDateTime.date]}
              onChange={handleChangeDate}
              rangePosition="end"
            />
          </Box>
          <Box sx={{ width: "225px", mt: "48px" }}>
            <TimeField label="Time" value={endDateTime.time} onChange={handleChangeTime} format="HH:mm:ss" />
          </Box>
        </Box>
      )}

      <Box sx={{ height: "24px", mt: "2px" }}>
        <Typography color="error">{error && error}</Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "end", gap: "6px" }}>
        <Button onClick={handleClose}>
          <Typography fontWeight="bold">Close</Typography>
        </Button>
        <Button onClick={handleSave}>
          <Typography fontWeight="bold">Ok</Typography>
        </Button>
      </Box>
    </Box>
  );
}
